import {
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    applyCameraState,
    applyCameraPreset,
    doesCameraMatchState,
    getResetViewCameraState,
    syncControlsResetState,
} from './renderables';
import { areBuildableSceneModelsSettled } from './sceneRenderableState';

function getRenderableHandles(renderHandlesRef) {
    return Array.from(renderHandlesRef.current.values())
        .map((entry) => entry.handle);
}

function useViewportCameraController({
    buildableSceneModels,
    cameraCommand,
    cameraRef,
    controlsRef,
    isViewportReady,
    onCameraViewStateChange,
    renderHandlesRef,
    renderStatuses,
    sceneModels,
    visibleBoundsVersion,
}) {
    const initialFitAppliedRef = useRef(false);
    const resetViewStateRef = useRef(null);
    const onCameraViewStateChangeRef = useRef(onCameraViewStateChange);
    const sceneModelIdsKey = useMemo(
        () => sceneModels.map((model) => model.id).join('|'),
        [sceneModels],
    );

    const updateResetViewBaseline = () => {
        const resetViewState = getResetViewCameraState(
            cameraRef.current,
            getRenderableHandles(renderHandlesRef),
        );

        resetViewStateRef.current = resetViewState;

        if (resetViewState) {
            syncControlsResetState(cameraRef.current, controlsRef.current, resetViewState);
        }

        return resetViewState;
    };

    const emitCameraViewStateChange = (resetViewState = resetViewStateRef.current) => {
        onCameraViewStateChangeRef.current?.({
            canResetView: Boolean(resetViewState)
                && !doesCameraMatchState(cameraRef.current, controlsRef.current, resetViewState),
        });
    };

    useEffect(() => {
        onCameraViewStateChangeRef.current = onCameraViewStateChange;
    }, [onCameraViewStateChange]);

    useEffect(() => {
        initialFitAppliedRef.current = false;
    }, [sceneModelIdsKey]);

    useEffect(() => {
        const resetViewState = updateResetViewBaseline();
        emitCameraViewStateChange(resetViewState);
    }, [isViewportReady, visibleBoundsVersion]);

    useEffect(() => {
        if (!isViewportReady || !controlsRef.current) {
            return undefined;
        }

        const handleControlsChange = () => {
            emitCameraViewStateChange();
        };

        controlsRef.current.addEventListener('change', handleControlsChange);
        emitCameraViewStateChange();

        return () => {
            controlsRef.current?.removeEventListener('change', handleControlsChange);
        };
    }, [controlsRef, isViewportReady]);

    useEffect(() => {
        if (!isViewportReady || initialFitAppliedRef.current || buildableSceneModels.length === 0) {
            return;
        }

        if (!areBuildableSceneModelsSettled(buildableSceneModels, renderStatuses)) {
            return;
        }

        const renderHandles = getRenderableHandles(renderHandlesRef);

        if (renderHandles.length === 0) {
            return;
        }

        const resetViewState = updateResetViewBaseline();

        applyCameraState(cameraRef.current, controlsRef.current, resetViewState);
        initialFitAppliedRef.current = true;
        emitCameraViewStateChange(resetViewState);
    }, [buildableSceneModels, cameraRef, controlsRef, isViewportReady, renderHandlesRef, renderStatuses]);

    useEffect(() => {
        if (!cameraCommand) {
            return;
        }

        const renderHandles = getRenderableHandles(renderHandlesRef);

        if (renderHandles.length === 0) {
            return;
        }

        if (cameraCommand.type === 'resetView') {
            const resetViewState = resetViewStateRef.current || updateResetViewBaseline();

            applyCameraState(cameraRef.current, controlsRef.current, resetViewState);
            emitCameraViewStateChange(resetViewState);
            return;
        }

        applyCameraPreset(
            cameraRef.current,
            controlsRef.current,
            renderHandles,
            cameraCommand.type,
        );
        emitCameraViewStateChange();
    }, [cameraCommand, cameraRef, controlsRef, renderHandlesRef]);
}

export default useViewportCameraController;
